import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { VRMHumanBoneName, VRMHumanBoneParentMap, VRMLoaderPlugin, VRMUtils, type VRM } from '@pixiv/three-vrm';
import {
  VRMAnimationLoaderPlugin,
  createVRMAnimationClip,
  type VRMAnimation,
} from '@pixiv/three-vrm-animation';

/**
 * 検証用（validateVrma）に VRM を読み込むためのフォールバック URL。呼び出し側で vrmUrl を
 * 省略した場合のみ使われる（通常は VrmContext が管理する選択中の VRM の URL が渡される）。
 * base 配下に配信されるため前置する。
 */
const SAMPLE_VRM_URL = `${import.meta.env.BASE_URL}samples/AvatarSample.vrm`;

/**
 * GEM-X（骨格推定）の直立時の推定 hips 高さ（メートル）。SOMA(SMPL系)の人体モデル基準。
 * free-assets のアニメ調キャラ3枚（lot-smoke: smoke-stand-01/dup-01）で実測したところ 0.9403 m
 * だった（2026-08-11。参考: しゃがみ smoke-stand-03 は 0.5307、片足立ち smoke-stand-02 は 0.7913）。
 * VRM側の身長には個体差があるため、この値を絶対値としてVRMへ入れるのではなく、これを1.0とした
 * 比率でVRM側のrest hips高さをスケールする（下記 exportPoseVrma 参照）。推定モデルを差し替えたら
 * この定数を測り直すこと。
 */
const SOMA_STANDING_HIPS_Y = 0.9403;

/** 片手分の指ボーンを親→子の順で並べたもの。TARGET_BONE_ORDER 組み立て用のヘルパー。 */
function fingerBones(prefix: 'Left' | 'Right'): VRMHumanBoneName[] {
  const b = VRMHumanBoneName;
  if (prefix === 'Left') {
    return [
      b.LeftThumbMetacarpal, b.LeftThumbProximal, b.LeftThumbDistal,
      b.LeftIndexProximal, b.LeftIndexIntermediate, b.LeftIndexDistal,
      b.LeftMiddleProximal, b.LeftMiddleIntermediate, b.LeftMiddleDistal,
      b.LeftRingProximal, b.LeftRingIntermediate, b.LeftRingDistal,
      b.LeftLittleProximal, b.LeftLittleIntermediate, b.LeftLittleDistal,
    ];
  }
  return [
    b.RightThumbMetacarpal, b.RightThumbProximal, b.RightThumbDistal,
    b.RightIndexProximal, b.RightIndexIntermediate, b.RightIndexDistal,
    b.RightMiddleProximal, b.RightMiddleIntermediate, b.RightMiddleDistal,
    b.RightRingProximal, b.RightRingIntermediate, b.RightRingDistal,
    b.RightLittleProximal, b.RightLittleIntermediate, b.RightLittleDistal,
  ];
}

/**
 * VRMA ノード階層として出力するボーン一覧（親→子の順）。
 * 指ボーンは各指ごとに Thumb: Metacarpal→Proximal→Distal、他4指: Proximal→Intermediate→Distal
 * の順（VRMHumanBoneParentMap の親子関係と一致）。VRM に存在しないボーンは
 * effectiveParent + existingSet の仕組みで自動的にスキップされる。
 */
const TARGET_BONE_ORDER: VRMHumanBoneName[] = [
  VRMHumanBoneName.Hips,
  VRMHumanBoneName.Spine,
  VRMHumanBoneName.Chest,
  VRMHumanBoneName.UpperChest,
  VRMHumanBoneName.Neck,
  VRMHumanBoneName.Head,
  VRMHumanBoneName.LeftShoulder,
  VRMHumanBoneName.LeftUpperArm,
  VRMHumanBoneName.LeftLowerArm,
  VRMHumanBoneName.LeftHand,
  ...fingerBones('Left'),
  VRMHumanBoneName.RightShoulder,
  VRMHumanBoneName.RightUpperArm,
  VRMHumanBoneName.RightLowerArm,
  VRMHumanBoneName.RightHand,
  ...fingerBones('Right'),
  VRMHumanBoneName.LeftUpperLeg,
  VRMHumanBoneName.LeftLowerLeg,
  VRMHumanBoneName.LeftFoot,
  VRMHumanBoneName.LeftToes,
  VRMHumanBoneName.RightUpperLeg,
  VRMHumanBoneName.RightLowerLeg,
  VRMHumanBoneName.RightFoot,
  VRMHumanBoneName.RightToes,
];

const FLOAT = 5126; // glTF componentType: FLOAT

interface GltfAccessor {
  bufferView: number;
  componentType: number;
  count: number;
  type: 'SCALAR' | 'VEC3' | 'VEC4';
  min?: number[];
  max?: number[];
}

interface GltfBufferView {
  buffer: 0;
  byteOffset: number;
  byteLength: number;
}

/**
 * glTF の accessor / bufferView と、それが指すバイナリ本体を積み上げていくビルダー。
 * 全データを Float32 で1本のバッファに連結する（float は4バイト単位なので、
 * 連結するだけで各 bufferView の byteOffset は自然に4バイトアラインされる）。
 */
class BinaryBuilder {
  private chunks: Uint8Array[] = [];
  private totalLength = 0;
  readonly bufferViews: GltfBufferView[] = [];
  readonly accessors: GltfAccessor[] = [];

  private pushBufferView(data: Float32Array): number {
    const bytes = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    const byteOffset = this.totalLength;
    this.chunks.push(bytes);
    this.totalLength += bytes.byteLength;
    const index = this.bufferViews.length;
    this.bufferViews.push({ buffer: 0, byteOffset, byteLength: bytes.byteLength });
    return index;
  }

  /** アニメーションのキーフレーム時刻用（SCALAR）。 */
  addTimeAccessor(times: number[]): number {
    const data = new Float32Array(times);
    const bufferView = this.pushBufferView(data);
    const index = this.accessors.length;
    this.accessors.push({
      bufferView,
      componentType: FLOAT,
      count: times.length,
      type: 'SCALAR',
      min: [Math.min(...times)],
      max: [Math.max(...times)],
    });
    return index;
  }

  addVec3Accessor(vectors: THREE.Vector3Tuple[]): number {
    const data = new Float32Array(vectors.length * 3);
    vectors.forEach((v, i) => data.set(v, i * 3));
    const bufferView = this.pushBufferView(data);
    const index = this.accessors.length;
    this.accessors.push({ bufferView, componentType: FLOAT, count: vectors.length, type: 'VEC3' });
    return index;
  }

  addVec4Accessor(vectors: THREE.Vector4Tuple[]): number {
    const data = new Float32Array(vectors.length * 4);
    vectors.forEach((v, i) => data.set(v, i * 4));
    const bufferView = this.pushBufferView(data);
    const index = this.accessors.length;
    this.accessors.push({ bufferView, componentType: FLOAT, count: vectors.length, type: 'VEC4' });
    return index;
  }

  build(): Uint8Array {
    const buf = new Uint8Array(this.totalLength);
    let offset = 0;
    for (const chunk of this.chunks) {
      buf.set(chunk, offset);
      offset += chunk.byteLength;
    }
    return buf;
  }
}

/** glTF JSON と BIN チャンクから GLB（バイナリ glTF）の Blob を組み立てる。 */
function buildGlb(json: unknown, binary: Uint8Array): Blob {
  const jsonBytes = new TextEncoder().encode(JSON.stringify(json));
  const jsonPad = (4 - (jsonBytes.byteLength % 4)) % 4;
  const jsonChunk = new Uint8Array(jsonBytes.byteLength + jsonPad);
  jsonChunk.set(jsonBytes);
  jsonChunk.fill(0x20, jsonBytes.byteLength); // JSON チャンクは空白でパディングする規約

  const binPad = (4 - (binary.byteLength % 4)) % 4;
  const binChunk = new Uint8Array(binary.byteLength + binPad);
  binChunk.set(binary); // BIN チャンクのパディングは 0 埋め（Uint8Array の初期値のまま）

  const totalLength = 12 + 8 + jsonChunk.byteLength + 8 + binChunk.byteLength;
  const buffer = new ArrayBuffer(totalLength);
  const dv = new DataView(buffer);
  let offset = 0;

  dv.setUint32(offset, 0x46546c67, true); // magic 'glTF'
  offset += 4;
  dv.setUint32(offset, 2, true); // version
  offset += 4;
  dv.setUint32(offset, totalLength, true); // 全体バイト長
  offset += 4;

  dv.setUint32(offset, jsonChunk.byteLength, true);
  offset += 4;
  dv.setUint32(offset, 0x4e4f534a, true); // chunkType 'JSON'
  offset += 4;
  new Uint8Array(buffer, offset, jsonChunk.byteLength).set(jsonChunk);
  offset += jsonChunk.byteLength;

  dv.setUint32(offset, binChunk.byteLength, true);
  offset += 4;
  dv.setUint32(offset, 0x004e4942, true); // chunkType 'BIN\0'
  offset += 4;
  new Uint8Array(buffer, offset, binChunk.byteLength).set(binChunk);
  offset += binChunk.byteLength;

  return new Blob([buffer], { type: 'model/gltf-binary' });
}

interface NodeDraft {
  name: string;
  translation: THREE.Vector3Tuple;
  children: number[];
}

/** buildRestNodeHierarchy() の戻り値。exportPoseVrma / exportAnimationVrma 共通の下ごしらえ結果。 */
interface RestHierarchy {
  /** VRM に実在するボーン（TARGET_BONE_ORDER のうち存在するものだけ、親→子の順）。 */
  existing: VRMHumanBoneName[];
  /** existing の各ボーン名 → 出力ノード配列でのインデックス。 */
  nodeIndex: Map<VRMHumanBoneName, number>;
  /** 出力する glTF ノード配列（index 0 はルート）。 */
  nodes: NodeDraft[];
  /**
   * レスト状態での hips のワールド高さ（y）。hips ノード自身のレスト translation は常に
   * [0,0,0]（ルートノードが hips のワールド位置を担っているため）で、実際のレスト高さは
   * このフィールドが持つ。exportPoseVrma が静止ポーズの絶対高さを計算する際に使う。
   */
  restHipsWorldY: number;
}

/**
 * VRM の正規化ヒューマノイドのレスト位置（T ポーズ）から、VRM ヒューマノイドの親子関係に沿った
 * 独自のノード階層を構築する。存在しないオプションボーン（chest 等）はスキップし、その子は
 * 最も近い存在する祖先ボーンへ張り替える。exportPoseVrma / exportAnimationVrma で共通の処理
 * （元 exportPoseVrma のノード階層構築部分をそのまま切り出したもの）。
 *
 * 各ノードのレスト回転は明示せず（= 恒等回転）に translation のみで階層を組む。これにより
 * VRMAnimationLoaderPlugin 側の「レスト時ワールド回転 = 恒等」という前提が満たされ、
 * rotation チャンネルの値をそのまま正規化ボーンへ適用する差分回転として書き込める
 * （@pixiv/three-vrm-animation の読み込み実装を確認して採用した設計）。この前提は
 * exportAnimationVrma（時系列アニメーション）でも変わらず利用する。
 */
function buildRestNodeHierarchy(vrm: VRM): RestHierarchy {
  // レスト状態に戻してからワールド位置を確定させる。
  vrm.humanoid.resetNormalizedPose();
  vrm.scene.updateMatrixWorld(true);

  const existing = TARGET_BONE_ORDER.filter((name) => vrm.humanoid.getNormalizedBoneNode(name) != null);
  const existingSet = new Set(existing);

  function effectiveParent(name: VRMHumanBoneName): VRMHumanBoneName | null {
    let p: VRMHumanBoneName | null = VRMHumanBoneParentMap[name];
    while (p != null && !existingSet.has(p)) {
      p = VRMHumanBoneParentMap[p];
    }
    return p;
  }

  const hipsBoneNode = vrm.humanoid.getNormalizedBoneNode(VRMHumanBoneName.Hips);
  if (!hipsBoneNode) throw new Error('VRM に hips ボーンが見つかりません');
  const hipsWorldPos = new THREE.Vector3();
  hipsBoneNode.getWorldPosition(hipsWorldPos);

  const worldPositions = new Map<VRMHumanBoneName, THREE.Vector3>();
  for (const name of existing) {
    const node = vrm.humanoid.getNormalizedBoneNode(name)!;
    const pos = new THREE.Vector3();
    node.getWorldPosition(pos);
    worldPositions.set(name, pos);
  }

  // ---- glTF ノード階層を構築 ----
  // index 0 はルート（hips の親。translation = hips のワールド位置）。
  const nodes: NodeDraft[] = [
    { name: 'root', translation: [hipsWorldPos.x, hipsWorldPos.y, hipsWorldPos.z], children: [] },
  ];
  const nodeIndex = new Map<VRMHumanBoneName, number>();
  for (const name of existing) {
    nodeIndex.set(name, nodes.length);
    nodes.push({ name, translation: [0, 0, 0], children: [] });
  }
  for (const name of existing) {
    const idx = nodeIndex.get(name)!;
    const parent = effectiveParent(name);
    const parentWorldPos = parent ? worldPositions.get(parent)! : hipsWorldPos;
    const worldPos = worldPositions.get(name)!;
    const local = new THREE.Vector3().subVectors(worldPos, parentWorldPos);
    nodes[idx].translation = [local.x, local.y, local.z];
    const parentIdx = parent ? nodeIndex.get(parent)! : 0;
    nodes[parentIdx].children.push(idx);
  }

  return { existing, nodeIndex, nodes, restHipsWorldY: hipsWorldPos.y };
}

/** exportAnimationVrma の1フレーム分。timeSec の昇順で渡すこと。 */
export interface VrmaAnimationFrame {
  /** そのフレームの再生時刻（秒）。 */
  timeSec: number;
  /** そのフレームのヒューマノイドポーズ（ボーン名 → ローカル回転）。全ボーンが揃っている必要はない。 */
  pose: Map<VRMHumanBoneName, THREE.Quaternion>;
  /**
   * そのフレームでの root（hips）のワールド位置。省略可能（サーバー未対応時など）。
   * exportAnimationVrma はこのうち y 成分のみを、rootTranslation を持つ最初のフレームを基準にした
   * "差分" として hips の translation チャンネルへ反映する。x/z（水平移動）は使わない
   * （この機能は「その場でポーズを切り替える」用途であり、キャラクターを歩かせる想定ではないため）。
   * 静止ポーズ（exportPoseVrma）はこの差分方式ではなく、buildVrmaGlb の
   * options.staticHipsTranslationY による絶対値方式を使う（0秒・1秒とも同一フレームのため
   * 「最初のフレームからの差分」が常に0になり、差分方式では腰の高さを表現できないため）。
   */
  rootTranslation?: { x: number; y: number; z: number };
}

/**
 * 共通の下ごしらえ（buildRestNodeHierarchy の結果）とフレーム列から VRMA の glTF JSON + バイナリを
 * 組み立てて GLB Blob を返す。exportPoseVrma（2キーの静止ポーズ）・exportAnimationVrma（時系列）
 * いずれもこの関数で glTF を組み立てる。
 *
 * あるボーンが一部のフレームにしか無い場合は、そのボーンが最後に出現した値を後続フレームへ
 * 引き継ぐ（未出現の間は恒等回転 [0,0,0,1] で埋める）ことでチャンネルの整合性を保つ。
 * 全フレームを通じて一度も出現しないボーンは、そのボーンのチャンネル自体を作らない
 * （exportPoseVrma が pose に無いボーンのチャンネルを省略していたのと同じ扱い）。
 *
 * options.staticHipsTranslationY を指定すると、hips の translation チャンネルは
 * frames の rootTranslation を無視し、全フレームで指定された y を絶対値としてそのまま使う
 * （静止ポーズ用の「絶対値」方式）。省略時は既存の「rootTranslation を持つ最初のフレームからの
 * 差分」方式（動画用）のまま（exportAnimationVrma はこちらを使い続ける）。
 */
function buildVrmaGlb(
  hierarchy: RestHierarchy,
  frames: VrmaAnimationFrame[],
  animationName: string,
  options?: { staticHipsTranslationY?: number },
): Blob {
  const { existing, nodeIndex, nodes } = hierarchy;

  const builder = new BinaryBuilder();
  const timeAccessor = builder.addTimeAccessor(frames.map((f) => f.timeSec));
  const channels: Array<{ sampler: number; target: { node: number; path: 'rotation' | 'translation' } }> = [];
  const samplers: Array<{ input: number; output: number; interpolation: 'LINEAR' }> = [];

  // hips の translation チャンネル。
  // - options.staticHipsTranslationY 指定時（静止ポーズ）: 全フレームその値を絶対値として使う。
  //   0秒・1秒とも同一姿勢の静止ポーズでは「最初のフレームからの差分」が常に0になってしまい
  //   差分方式では腰の高さを表現できないため、こちらは絶対値をそのまま書き込む。
  // - 未指定時（動画）: rootTranslation を持つ最初のフレームの y を基準 0 とした差分のみを
  //   y に入れる（x/z は常に 0 = 水平移動なし）。誰も rootTranslation を持たなければ全フレーム
  //   [0,0,0] となり、レスト高さ固定になる。
  {
    const staticY = options?.staticHipsTranslationY;
    let values: THREE.Vector3Tuple[];
    if (staticY !== undefined) {
      values = frames.map((): THREE.Vector3Tuple => [0, staticY, 0]);
    } else {
      const baselineY = frames.find((f) => f.rootTranslation)?.rootTranslation?.y ?? 0;
      values = frames.map((f): THREE.Vector3Tuple =>
        f.rootTranslation ? [0, f.rootTranslation.y - baselineY, 0] : [0, 0, 0],
      );
    }
    const output = builder.addVec3Accessor(values);
    samplers.push({ input: timeAccessor, output, interpolation: 'LINEAR' });
    channels.push({
      sampler: samplers.length - 1,
      target: { node: nodeIndex.get(VRMHumanBoneName.Hips)!, path: 'translation' },
    });
  }

  for (const name of existing) {
    let lastQuat: THREE.Vector4Tuple | null = null;
    let anyDefined = false;
    const values: THREE.Vector4Tuple[] = frames.map((f) => {
      const q = f.pose.get(name);
      if (q) {
        anyDefined = true;
        lastQuat = [q.x, q.y, q.z, q.w];
      }
      // このボーンがまだ一度も出現していない先頭区間は恒等回転で埋める。
      return lastQuat ?? [0, 0, 0, 1];
    });
    if (!anyDefined) continue;
    const output = builder.addVec4Accessor(values);
    samplers.push({ input: timeAccessor, output, interpolation: 'LINEAR' });
    channels.push({ sampler: samplers.length - 1, target: { node: nodeIndex.get(name)!, path: 'rotation' } });
  }

  // ---- VRMC_vrm_animation 拡張の humanoid.humanBones ----
  const humanBones: Record<string, { node: number }> = {};
  for (const name of existing) {
    humanBones[name] = { node: nodeIndex.get(name)! };
  }

  const binary = builder.build();
  const json = {
    asset: { version: '2.0', generator: 'vrma-hub pose capture' },
    extensionsUsed: ['VRMC_vrm_animation'],
    extensions: {
      VRMC_vrm_animation: {
        specVersion: '1.0',
        humanoid: { humanBones },
      },
    },
    scene: 0,
    scenes: [{ nodes: [0] }],
    nodes: nodes.map((n) => ({
      name: n.name,
      translation: n.translation,
      ...(n.children.length > 0 ? { children: n.children } : {}),
    })),
    animations: [{ name: animationName, channels, samplers }],
    accessors: builder.accessors,
    bufferViews: builder.bufferViews,
    buffers: [{ byteLength: binary.byteLength }],
  };

  return buildGlb(json, binary);
}

/**
 * 静止ポーズを表す VRMA（VRMC_vrm_animation 拡張つき glTF、GLB形式）を書き出す。
 * 0秒・1秒の2キーで同一姿勢を保つ rotation / translation チャンネルを持たせた
 * exportAnimationVrma の特殊ケースとして実装している（詳細は buildRestNodeHierarchy /
 * buildVrmaGlb 参照）。
 *
 * rootTranslation を渡すと、その y（GEM-X 推定の hips ワールド高さ）を SOMA_STANDING_HIPS_Y
 * （直立時の推定値）に対する比率に換算し、その比率で VRM 側の rest hips 高さをスケールした
 * 絶対値を hips の translation チャンネルへ書き込む（座り・しゃがみ等で腰の高さが下がらず
 * 床に浮く問題への対応）。省略時は従来どおり VRM のレスト高さのまま（hips translation は
 * 実質 [0,0,0]）。x/z は使わない（buildVrmaGlb の rootTranslation コメントと同じ理由）。
 */
export function exportPoseVrma(
  vrm: VRM,
  pose: Map<VRMHumanBoneName, THREE.Quaternion>,
  rootTranslation?: { x: number; y: number; z: number },
): Blob {
  const hierarchy = buildRestNodeHierarchy(vrm);
  let staticHipsTranslationY: number | undefined;
  if (rootTranslation) {
    // GEM-X推定の「直立時の高さ」に対するこのポーズの比率を、VRM側のrest hips高さに掛けて
    // 絶対高さを求める（VRM側の身長には個体差があるため、推定値をそのまま絶対値として
    // 入れるのではなく比率でスケールする）。
    const ratio = rootTranslation.y / SOMA_STANDING_HIPS_Y;
    // hipsノードのレストtranslationは常に[0,0,0]で、実際のレスト高さはrootノード
    // （hierarchy.restHipsWorldY）が担っている。translationチャンネルの値はhipsノードの
    // レストtranslationを完全に上書きするため、最終的なワールド高さ = restHipsWorldY +
    // channelValue になる。目標のワールド高さ = ratio * restHipsWorldY なので、
    // channelValue = (ratio - 1) * restHipsWorldY と逆算する。
    staticHipsTranslationY = (ratio - 1) * hierarchy.restHipsWorldY;
  }
  return buildVrmaGlb(
    hierarchy,
    [
      { timeSec: 0, pose },
      { timeSec: 1, pose },
    ],
    'pose',
    staticHipsTranslationY !== undefined ? { staticHipsTranslationY } : undefined,
  );
}

/**
 * 複数枚の画像から推定した時系列のキーポーズを1本の VRMA（時系列アニメーション）として書き出す。
 * exportPoseVrma と同じノード階層構築（buildRestNodeHierarchy）を使い、そこへ frames の枚数ぶんの
 * キーフレームを積む点だけが異なる（詳細は buildVrmaGlb 参照）。
 *
 * frames は timeSec の昇順で渡すこと。あるフレームに存在しないボーンがあっても、そのボーンの
 * チャンネルは直前の値を引き継いで埋めるため出力は壊れない（buildVrmaGlb 参照）。
 */
export function exportAnimationVrma(
  vrm: VRM,
  frames: VrmaAnimationFrame[],
  options?: { name?: string },
): Blob {
  if (frames.length === 0) {
    throw new Error('frames が空です。1件以上のキーフレームが必要です。');
  }
  const hierarchy = buildRestNodeHierarchy(vrm);
  return buildVrmaGlb(hierarchy, frames, options?.name ?? 'animation');
}

function createValidationLoader(): GLTFLoader {
  const loader = new GLTFLoader();
  loader.register((parser) => new VRMLoaderPlugin(parser));
  loader.register((parser) => new VRMAnimationLoaderPlugin(parser));
  return loader;
}

/**
 * exportPoseVrma が書き出した Blob を実際に読み戻して自己検証する。
 * VRM を新規に読み込み、VRMAnimationLoaderPlugin で1件の VRMAnimation が
 * 取得できること、createVRMAnimationClip がエラーなくクリップ（トラックを持つ）を
 * 返すことを確認する。問題があれば例外を投げる。
 * vrmUrl は通常 VrmContext が管理する選択中の VRM の URL（省略時はサンプル VRM にフォールバック）。
 */
export async function validateVrma(blob: Blob, vrmUrl: string = SAMPLE_VRM_URL): Promise<void> {
  const vrmGltf = await createValidationLoader().loadAsync(vrmUrl);
  const vrm = vrmGltf.userData.vrm as VRM;

  const url = URL.createObjectURL(blob);
  try {
    const vrmaGltf = await createValidationLoader().loadAsync(url);
    const vrmAnimations = vrmaGltf.userData.vrmAnimations as VRMAnimation[] | undefined;
    if (!vrmAnimations || vrmAnimations.length !== 1) {
      throw new Error(`VRMAnimation の件数が想定と異なります（${vrmAnimations?.length ?? 0} 件）`);
    }
    const clip = createVRMAnimationClip(vrmAnimations[0], vrm);
    if (!clip || clip.tracks.length === 0) {
      throw new Error('VRMAnimationClip の生成に失敗しました（トラックが空です）');
    }
  } finally {
    URL.revokeObjectURL(url);
    VRMUtils.deepDispose(vrm.scene);
  }
}
